import * as React from "react";
import DataTable from "react-data-table-component";
import {
  ExpandableRowsComponent,
  TableColumn
} from "react-data-table-component/dist/DataTable/types";
import _ from "lodash";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Button } from "react-bootstrap";
import exportFromJSON from "export-from-json";
import { ExportType } from "export-from-json/dist/types/types";
import {
  IExportColumnMap,
  PagedResultsRequest
} from "../../common/sharedTypes";
import Pagination from "./components/Pagination";
import "./index.css";

interface IAutoTable {
  autoTable: (content: any) => void;
}

interface IPagedDatatableProps {
  columns: TableColumn<any>[];
  pagingCriteria: PagedResultsRequest;
  data: any[];
  totalDataItems: number;
  loadingData: boolean;
  updatePagedResults: (pagingCriteria: PagedResultsRequest) => void;
  getExportData: () => Promise<any[]>;
  exportTitle: string;
  exportFileName: string;
  exportColumnMaps: IExportColumnMap[];
  expandableRows?: boolean;
  expandableRowsComponent?: ExpandableRowsComponent<any>;
  selectableRows?: boolean;
  onSelectedRowsChanged?: (selectedRows: any[]) => void;
  selectableRowSelected?: (row) => boolean;
  hidePaging?: boolean;
  customStyles?: any;
  paginationComponentOptions?: any;
}

interface IPagedDatatableState {
  searchCriteria: string;
  exportingCSV: boolean;
  exportingPDF: boolean;
  exportingExcel: boolean;
}

export default class PagedDatatable extends React.Component<
  IPagedDatatableProps,
  IPagedDatatableState
> {
  private debounceUpdatePagedResults: () => void;

  constructor(props: IPagedDatatableProps) {
    super(props);

    this.state = {
      searchCriteria: (props.pagingCriteria?.searchCriteria ?? "") as string,
      exportingCSV: false,
      exportingPDF: false,
      exportingExcel: false
    };

    this.debounceUpdatePagedResults = _.debounce(() => {
     this.props.updatePagedResults({
        ...this.props.pagingCriteria,
        pageNumber: 1,
        searchCriteria: this.state.searchCriteria
      });
    }, 500);
  }

  componentDidUpdate(prevProps: IPagedDatatableProps) {
    const prev = (prevProps.pagingCriteria?.searchCriteria ?? "") as string;
    const next = (this.props.pagingCriteria?.searchCriteria ?? "") as string;

    // If parent restores criteria (or resets), sync the input UI
    if (prev !== next && next !== this.state.searchCriteria) {
      this.setState({ searchCriteria: next });
    }
  }

  handleSearchChange = (e) => {
    const value = e.target.value;
    this.setState({ searchCriteria: value }, () => {
      this.debounceUpdatePagedResults();
    });

  };

   handlePageChange = (page) => {
    this.props.updatePagedResults({
      ...this.props.pagingCriteria,
      pageNumber: page
    });
  };

  handlePerRowsChange = async (newPerPage, page) => {
    this.props.updatePagedResults({
      ...this.props.pagingCriteria,
      pageNumber: page,
      itemsPerPage: newPerPage
    });
  };

handleSort = (selectedColumn: any, sortDirection: any) => {
  this.props.updatePagedResults({
    ...this.props.pagingCriteria,
    pageNumber: 1,
    orderByField: selectedColumn.sortField,
    orderDescending: sortDirection == "desc"
  });
};


  downloadPDF = async () => {
    this.setState({ exportingPDF: true });
    try {
      const data = await this.props.getExportData();
      const unit = "pt";
      const size = "A4"; // Use A1, A2, A3 or A4
      const orientation = "landscape"; // portrait or landscape
      const doc = new jsPDF(orientation, unit, size);
      doc.setFontSize(15);

      const title = this.props.exportTitle;
      const headers = [this.props.exportColumnMaps.map((cm) => cm.columnName)];

      const formattedData = data.map((x) =>
        this.props.exportColumnMaps.map((cm) =>
          cm.convertMethod
            ? cm.convertMethod(x[cm.propertyName])
            : x[cm.propertyName]
        )
      );

      doc.text(title, 40, 40);
      doc.setFontSize(12);

      let content = {
        startY: 50,
        head: headers,
        body: formattedData
      };

      (doc as any as IAutoTable).autoTable(content);
      doc.save(this.props.exportFileName + ".pdf");
    } catch (ex) {
      console.log(ex);
    } finally {
      this.setState({ exportingPDF: false });
    }
  };

  downloadExport = async (type: ExportType) => {
    this.setState({
      exportingCSV: type == exportFromJSON.types.csv ? true : false,
      exportingExcel: type == exportFromJSON.types.csv ? false : true
    });
    try {
      const data = await this.props.getExportData();
      const formattedData = data.map((x) => {
        const d = {};

        this.props.exportColumnMaps.forEach(
          (cm) =>
            (d[cm.columnName] =
              (cm.convertMethod
                ? cm.convertMethod(x[cm.propertyName])
                : x[cm.propertyName]) || "")
        );
        return d;
      });
      exportFromJSON({
        data: formattedData,
        fileName: this.props.exportFileName,
        exportType: type
      });
    } catch (ex) {
      console.log(ex);
    } finally {
      this.setState({ exportingExcel: false, exportingCSV: false });
    }
  };

  
  public render() {
    //component doesn't offer built int alt row styling
    //Had to manually create
    const conditionalRowStyles = [
      {
        when: (row) => {
          return row.rowIndex % 2 == 0;
        },
        style: {
          backgroundColor: "rgba(0,0,0,.05)"
        }
      }
    ];

    const rowData = (this.props.data || []).map((row, index) => {
      return { ...row, rowIndex: index };
    });

    const pageNumber = this.props.pagingCriteria?.pageNumber ?? 1;
    const itemsPerPage = this.props.pagingCriteria?.itemsPerPage ?? 10;

    
    const orderDescending = this.props.pagingCriteria?.orderDescending ?? false;

   const orderByField = (this.props.pagingCriteria?.orderByField ?? "").toLowerCase();
    const sortCol = (this.props.columns || []).find((c: any) =>
      (c.sortField ?? "").toLowerCase() === orderByField
    );
    const defaultSortFieldId = sortCol?.id;
    

// Force re-mount when these change so pagination/sort UI updates reliably
const tableKey = `${pageNumber}|${itemsPerPage}|${orderByField ?? ""}|${orderDescending ? "desc" : "asc"}`;

    return (
      <div>
        <div style={{ float: "right" }}>
          <label className="searchTextLabel">
            Search:
            <input
              type="search"
              className="searchText"
              value={this.state.searchCriteria}
              onChange={this.handleSearchChange}
            />
          </label>
          <button
            className="btn btn-outline-primary btn-sm"
            style={{ fontSize: "12px", marginLeft: "5px" }}
            title="Refresh Data"
            disabled={this.props.loadingData}
            onClick={() => {
              this.props.updatePagedResults({ ...this.props.pagingCriteria });
            }}
          >
            <i className="fa fa-refresh" />
          </button>
        </div>
        <DataTable
          //tableId={this.props.id || 'dt_unnamed'}
         key={tableKey}
          columns={this.props.columns}
          data={rowData || []}
          progressPending={this.props.loadingData}
          progressComponent={
            <div className="loadingSpinner">
              <i className="fas fa-sync fa-spin" />
              &nbsp;Loading...
            </div>
          }
          pagination={this.props.hidePaging ? false : true} 
          paginationServer
          paginationTotalRows={this.props.totalDataItems}
          paginationComponent={Pagination}
          paginationPerPage={itemsPerPage}
          paginationDefaultPage={pageNumber}
          paginationComponentOptions={this.props.paginationComponentOptions}
          sortServer
          defaultSortAsc={!this.props.pagingCriteria?.orderDescending}
          {...(defaultSortFieldId ? { defaultSortFieldId } : {})}
          onChangePage={this.handlePageChange}
          onChangeRowsPerPage={this.handlePerRowsChange}
          onSort={this.handleSort}
          style={{ overflowY: "visible", overflowX: "visible" }}
          conditionalRowStyles={conditionalRowStyles}
          expandableRows={this.props.expandableRows}
          expandableRowsComponent={this.props.expandableRowsComponent}
          selectableRows={this.props.selectableRows}
          onSelectedRowsChange={(e) =>
            this.props.onSelectedRowsChanged &&
            this.props.onSelectedRowsChanged(e.selectedRows)
          }
          selectableRowSelected={this.props.selectableRowSelected}
          customStyles={this.props.customStyles}
        />
        {!this.props.loadingData &&
          this.props.data &&
          this.props.data.length > 0 && (
            <div style={{ textAlign: "center" }}>
              <Button
                onClick={() => {
                  this.downloadExport(exportFromJSON.types.xls);
                }}
                disabled={
                  this.state.exportingPDF ||
                  this.state.exportingCSV ||
                  this.state.exportingExcel
                }
              >
                {this.state.exportingExcel
                  ? "Generating Excel File..."
                  : "Excel"}
              </Button>
              &nbsp;
              <Button
                onClick={() => {
                  this.downloadExport(exportFromJSON.types.csv);
                }}
                disabled={
                  this.state.exportingPDF ||
                  this.state.exportingCSV ||
                  this.state.exportingExcel
                }
              >
                {this.state.exportingCSV ? "Generating CSV File..." : "CSV"}
              </Button>
              &nbsp;
              <Button
                onClick={this.downloadPDF}
                disabled={
                  this.state.exportingPDF ||
                  this.state.exportingCSV ||
                  this.state.exportingExcel
                }
              >
                {this.state.exportingPDF ? "Generating PDF File..." : "Print"}
              </Button>
            </div>
          )}
      </div>
    );
  }
}
